import React from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import {Box, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";

// Top of the y-axis: a bit of headroom over the highest value, rounded up to the shown decimals.
// The top is kept an even number of steps so the middle label (top / 2) is exact too.
const resolveTop = (maxValue, decimals) => {
    const factor = 10 ** decimals;
    let steps = Math.ceil(maxValue * 1.15 * factor) || factor;
    if (steps % 2 === 1) steps += 1;
    return steps / factor;
};

const axisLabelSx = {
    position: "absolute",
    left: 8,
    fontSize: 10,
    lineHeight: 1,
    color: grey[400],
    pointerEvents: "none",
};

// decimals: rounding of the y-axis labels and tooltip value; showYAxis: draw the labels inside the plot
const AreaChart = ({
                       data = [], colors = ["#3f51b5"], height = 90, width = "100%",
                       decimals = 2, showYAxis = false
                   }) => {
    // Generate default data if empty
    if (data.length === 0) {
        const now = moment();
        data = Array.from({length: 5}, (_, i) => ({
            dateCreated: now.clone().subtract(4 - i, "minutes").toISOString(),
            duration: 0,
        }));
    }

    // Sort data by dateCreated (oldest to newest)
    const sortedData = [...data].sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));

    const seriesData = sortedData.map((item) => ({
        x: moment(item.dateCreated).valueOf(),
        y: item.duration,
    }));

    const maxValue = Math.max(0, ...sortedData.map(d => Number(d.duration) || 0));
    const top = resolveTop(maxValue, decimals);

    const options = {
        chart: {
            type: "area",
            sparkline: {enabled: true},
            toolbar: {show: false},
            zoom: {enabled: false},
            animations: {enabled: false},
        },
        yaxis: {
            // Fixed scale so the overlaid labels line up with the plot; an all-zero line is
            // lifted off the bottom edge so it isn't clipped
            min: maxValue > 0 ? 0 : -1,
            max: top,
            labels: {show: false},
            axisBorder: {show: false},
            axisTicks: {show: false},
        },
        xaxis: {
            type: "datetime",
            labels: {show: false},
            axisBorder: {show: false},
            axisTicks: {show: false},
        },
        grid: {show: false},
        fill: {
            type: "solid",
            opacity: 0.10,
        },
        colors,
        stroke: {curve: "smooth", width: 2},
        dataLabels: {enabled: false},
        markers: {size: 0},
        tooltip: {
            custom: function ({seriesIndex, dataPointIndex}) {
                const dataPoint = sortedData[dataPointIndex];
                if (!dataPoint || dataPoint.duration === null) {
                    return `
                        <div style="padding: 8px 12px; background: white; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.12);">
                            <div style="font-weight: 600; color: #999; font-size: 12px;">No data</div>
                        </div>
                    `;
                }
                const formattedDate = moment(dataPoint.dateCreated).format("DD MMM HH:mm");
                const value = (Number(dataPoint.duration) || 0).toFixed(decimals);
                return `
                    <div style="padding: 8px 12px; background: white; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.12); white-space: nowrap;">
                        <div style="font-weight: 600; margin-bottom: 4px; color: #333; font-size: 12px;">${formattedDate}</div>
                        <div style="color: #666; font-size: 13px;">Value: <strong>${value}</strong></div>
                    </div>
                `;
            },
        },
    };
    return (
        <Box sx={{position: "relative", height}}>
            <Chart
                options={options}
                series={[{name: "Duration", data: seriesData}]}
                type="area"
                height={height}
                width={width}
            />
            {/* Y-axis labels drawn inside the plot (sparkline mode has no axis of its own) */}
            {showYAxis && (
                <>
                    <Typography sx={{...axisLabelSx, top: 4}}>{top.toFixed(decimals)}</Typography>
                    <Typography sx={{...axisLabelSx, top: "50%", transform: "translateY(-50%)"}}>
                        {(top / 2).toFixed(decimals)}
                    </Typography>
                    <Typography sx={{...axisLabelSx, bottom: 4}}>{(0).toFixed(decimals)}</Typography>
                </>
            )}
        </Box>
    );
};

export default AreaChart;
